/**
 * Thin fetch wrapper around `GET /v1/me/entitlements`. Reuses the shared API
 * base + Firebase ID-token + TransformInterceptor unwrapping conventions
 * (mirrors `modules/analytics/api.ts`). The page consumes this through the
 * `usePublishEntitlement` hook so React never sees fetch shapes directly.
 */
import { API_BASE } from '@/src/infrastructure/lib/api';
import { auth } from '@/src/infrastructure/config/firebase';
import type { PublishEntitlementView } from './types';

async function authFetch<T>(url: string): Promise<T> {
  const isBypass = import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';
  const token = isBypass ? 'dev-token' : await auth.currentUser?.getIdToken().catch(() => null);
  if (!token) {
    throw new Error('Not authenticated');
  }
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.message ?? `HTTP ${res.status}`);
  }
  const body = await res.json();
  return (body?.data !== undefined ? body.data : body) as T;
}

/**
 * ⛔ **The backend currently IGNORES both parameters (finding F-03).**
 *
 * Intended behaviour, restored by the Phase 6 `InvitationEntitlement` cutover:
 * `templateId` scopes the view to one design, so purchased credits only publish
 * the template they were bought for (beta/admin credits publish any), and
 * `invitationId` further excludes a credit bought for a different invitation.
 *
 * Today the response is the **account-wide total** whether or not they are
 * sent. They are still sent so the call sites do not have to change twice, but
 * do not render UI that promises design-scoped credits, and do not treat a
 * non-zero `remainingCredits` here as proof the user can publish *this*
 * design — they may be holding a credit bought for something else.
 */
export function fetchPublishEntitlement(templateId?: string, invitationId?: string) {
  const params = new URLSearchParams();
  if (templateId) params.set('templateId', templateId);
  // Intended (Phase 6): a credit bought for ANOTHER invitation must not count
  // for this one. Sent today, ignored server-side — see the banner above.
  if (templateId && invitationId) params.set('invitationId', invitationId);
  const query = params.toString();
  return authFetch<PublishEntitlementView>(`${API_BASE}/me/entitlements${query ? `?${query}` : ''}`);
}
